/**
 * Web Push on this device (M2.1). Subscribing happens ONLY from a user tap (enablePush); on every app start, if permission is
 * already granted, the subscription is re-posted (or re-created) so the server's copy stays current. iOS: push works only on
 * 16.4+ when ContentOps is opened from the Home Screen.
 */
import { useEffect } from "react";
import { navigate } from "../app/router";
import type { ApiClient } from "../api/client";
import { useApi } from "../data/hooks";

export function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padded = (b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export type PushSupport = "supported" | "ios_needs_home_screen" | "unsupported";

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}
export function isStandalone(): boolean {
  return (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function pushSupport(): PushSupport {
  if (isIos() && !isStandalone()) return "ios_needs_home_screen";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return "supported";
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) throw new Error("The app's service worker isn't active yet — reload ContentOps once and try again.");
  return reg;
}

async function post(api: ApiClient, sub: PushSubscription): Promise<void> {
  const j = sub.toJSON() as { endpoint?: string; expirationTime?: number | null; keys?: { p256dh?: string; auth?: string } };
  await api.pushSubscribe({ endpoint: j.endpoint ?? sub.endpoint, expirationTime: j.expirationTime ?? null, keys: { p256dh: j.keys?.p256dh ?? "", auth: j.keys?.auth ?? "" }, user_agent: navigator.userAgent.slice(0, 500) });
}

async function subscribe(api: ApiClient, reg: ServiceWorkerRegistration): Promise<PushSubscription> {
  const key = await api.pushPublicKey();
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) as BufferSource });
}

/** From a user tap only: ask permission, subscribe, register the subscription with the server. */
export async function enablePush(api: ApiClient): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "Notifications are blocked for ContentOps. Allow them in the browser's site settings." : "Notifications weren't allowed.");
  const reg = await registration();
  const sub = (await reg.pushManager.getSubscription()) ?? (await subscribe(api, reg));
  await post(api, sub);
}

export async function disablePush(api: ApiClient): Promise<void> {
  const reg = await registration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await api.pushUnsubscribe(endpoint);
}

/** This device's current state: null = can't tell (no SW / unsupported). */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "supported") return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

/** App start: permission already granted → re-POST the subscription, or subscribe again if the browser dropped it. */
export async function resyncPush(api: ApiClient): Promise<"posted" | "resubscribed" | "skipped"> {
  if (pushSupport() !== "supported" || Notification.permission !== "granted") return "skipped";
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return "skipped";
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await subscribe(api, reg));
  await post(api, sub);
  return existing ? "posted" : "resubscribed";
}

export function usePushResync(): void {
  const api = useApi();
  useEffect(() => {
    void resyncPush(api).catch(() => undefined);
    // notificationclick in the SW focuses this window and asks it to route (no full reload).
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; link?: string } | null;
      if (d?.type === "contentops:navigate" && typeof d.link === "string" && d.link.startsWith("/")) navigate(d.link);
    };
    navigator.serviceWorker?.addEventListener?.("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMessage);
  }, [api]);
}
